/**
 * mainTabRegistry — sub-activity 가 열려있을 때 그 아래에 계속 마운트되어야 하는
 * "parent main tab" 컴포넌트 매핑.
 *
 * Layout 이 sub-activity URL 일 때 RR Outlet 대신 이 표에서 부모 main tab 컴포넌트를
 * 직접 렌더한다. 이렇게 하면 같은 JSX position 에 같은 컴포넌트가 유지되어 React 가
 * remount 하지 않는다 (= 스크롤/필터/입력 보존).
 *
 * 라우터 swap 대신 visibility 토글 기반의 KeepAlive 효과.
 */
import { memo, type ReactNode } from "react";
import HomePage from "../../pages/home/HomePage";
import SearchPage from "../../pages/card/SearchPage";
import CollectionsPage from "../../pages/collection/CollectionsPage";
import NotificationCenterPage from "../../pages/notification/NotificationCenterPage";
import MyPage from "../../pages/me/MyPage";
import RequireAuth from "./RequireAuth";

// 메인 탭 = 홈·검색·스캔·컬렉션·내정보 (기획확정안 §7).
// notifications 는 탭이 아니지만 sub-activity(/notifications/:id) 의 keep-alive 부모로 유지.
// ⚠ scan 은 keep-alive 탭 슬롯이 아니라 **전체화면 activity** 로 뜬다 (2026-07-31 —
//   카메라 수명을 mount/unmount 에 맡김). 그래서 이 레지스트리에서 제외.
export type MainTabKind = "home" | "search" | "collections" | "me" | "notifications";

/**
 * 안정적인 main-tab 래퍼들. memo + 빈 props → 재렌더 안 됨, 자식 재마운트도 안 됨.
 */
const HomeSlot = memo(function HomeSlot() {
  return <HomePage />;
});
const SearchSlot = memo(function SearchSlot() {
  return <SearchPage />;
});
const CollectionsSlot = memo(function CollectionsSlot() {
  return <CollectionsPage />;
});
const NotificationsSlot = memo(function NotificationsSlot() {
  return (
    <RequireAuth>
      <NotificationCenterPage />
    </RequireAuth>
  );
});
const MySlot = memo(function MySlot() {
  return (
    <RequireAuth>
      <MyPage />
    </RequireAuth>
  );
});

/** Sub-activity 의 부모 kind → 해당 main tab 컴포넌트. */
export function renderMainTabByKind(kind: string | null | undefined): ReactNode {
  if (!kind) return null;
  switch (kind) {
    case "home": return <HomeSlot />;
    case "search": return <SearchSlot />;
    case "collections": return <CollectionsSlot />;
    case "notifications": return <NotificationsSlot />;
    case "me": return <MySlot />;
    default: return null;
  }
}

/**
 * URL pathname → main tab kind. activity 테이블에 없는 URL 도 처리 가능.
 *
 * 플로우 단위로 매핑:
 *   - home:          /
 *   - notifications: /notifications*
 *   - me flow:       /me/*, /support/* (support 루트 제외 — cross-cutting public)
 */
export function inferMainTabKindFromPath(pathname: string): MainTabKind | null {
  if (pathname === "/") return "home";
  if (pathname === "/search" || pathname.startsWith("/search/")) return "search";
  // /scan 은 activity — 부모 탭 추론 없음 (hydration 이 rule.fallbackPath="/"를 쓰게 한다)
  if (pathname === "/collections" || pathname.startsWith("/collections/")) return "collections";
  if (pathname === "/notifications" || pathname.startsWith("/notifications/")) {
    return "notifications";
  }
  if (pathname === "/me" || pathname.startsWith("/me/")) return "me";
  // /support 는 어디서든 진입 가능한 cross-cutting public 페이지이므로 main tab 추론
  // 결과를 null 로 두어 MySlot (RequireAuth wrapping) 이 parent slot 으로 강제되지
  // 않도록 한다. /support/inquiry/* 같은 인증 필요 페이지는 SUB_ACTIVITY_RESOLVERS
  // 의 wrapAuth 가 개별적으로 게이트한다.
  if (pathname === "/support") return null;
  if (pathname.startsWith("/support/")) return "me";
  return null;
}
